// React
import React from 'react';
import { Controller } from 'react-hook-form';
// material-ui components
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import ImageAndTextSlideArray from '../shared/ImageAndTextSlideArray';
import JobAideArray from '../shared/JobAideArray';
import PEFeedbackArray from './components/PEFeedbackArray';
import ImageAttribution from '../shared/ImageAttribution';

const PEDebrief = (props) => {
  const { control, formState, watch } = props.methods;
  const { errors } = formState;
  const { inputDir, possiblePages } = props;

  return (
    <div>
      <NumberedHeading
        number="1"
        title="Label"
        instructions='Select either "ELO 1", "ELO 2", or "FPE".'
        required
      />
      <Controller
        control={control}
        name="content.core.label"
        render={({ field }) => (
          <FormControl
            fullWidth
            className="mt-16"
            size="small"
            error={!!errors.content?.core?.label}
            required
          >
            <InputLabel id="select-label">Label</InputLabel>
            <Select
              {...field}
              labelId="select-label"
              id="label"
              label="Label"
              classes={{ select: 'flex items-center space-x-12' }}
              defaultValue=""
            >
              <MenuItem value="" className="space-x-12" disabled>
                Select a label
              </MenuItem>
              <MenuItem value="ELO 1" className="space-x-12">
                ELO 1
              </MenuItem>
              <MenuItem value="ELO 2" className="space-x-12">
                ELO 2
              </MenuItem>
              <MenuItem value="FPE" className="space-x-12">
                FPE
              </MenuItem>
            </Select>
            {!!errors.content?.core?.label ? (
              <FormHelperText>
                {errors.content?.core?.label?.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="2"
        title="Image"
        instructions="Provide the objective image information."
      />
      <Controller
        control={control}
        name="content.core.media"
        render={({ field }) => (
          <ImageAttribution
            targetObj="content.core.media"
            inputDir={inputDir}
            errors={errors.content?.core?.media}
            {...{ control }}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="3"
        title="Message Text (TL)"
        instructions="Message for this PE in the Target Language."
      />
      <Controller
        control={control}
        name="content.core.text_tl"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Text (TL)"
            placeholder="Text (TL)"
            id="text"
            error={!!errors.content?.core?.text_tl}
            helperText={errors.content?.core?.text_tl?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
            dir={inputDir}
          />
        )}
      />

      <NumberedHeading
        number="4"
        title="Message Text (EN)"
        instructions="Message for this PE in English."
      />
      <Controller
        control={control}
        name="content.core.text_en"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Text (EN)"
            placeholder="Text (EN)"
            id="text"
            error={!!errors.content?.core?.text_en}
            helperText={errors.content?.core?.text_en?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
            required
          />
        )}
      />

      <NumberedHeading
        number="1"
        title="Environment"
        instructions="Insert the page ID for the main enviornment used in this Practical Exercise."
      />
      <Controller
        control={control}
        name="content.core.environment"
        render={({ field }) => (
          <>
            <FormControl
              className="mt-16"
              fullWidth
              size="small"
              error={!!errors.content?.core?.environment}
            >
              <InputLabel id="environment-page-select-label">
                Environment Page
              </InputLabel>
              <Select
                labelId="environment-page-select-label"
                id="environment-page-select"
                {...field}
                label="environment Page"
                value={
                  possiblePages.find((page) => page.id === field.value)
                    ? field.value
                    : ''
                }
              >
                <MenuItem value="" disabled>
                  <em>Environment Page</em>
                </MenuItem>
                {possiblePages.map((page) => (
                  <MenuItem key={page.id} value={page.id}>
                    {page.label} - {page.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.content?.core?.environment?.message}
              </FormHelperText>
            </FormControl>
          </>
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="2"
        title="Final Practical Exercise"
        instructions="Check the box if this debrief is for a Final Practical Exercise."
      />
      <Controller
        control={control}
        name="content.core.fpe"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(ev) => {
                  field.onChange(ev.target.checked);
                }}
                checked={field.value}
              />
            }
            label="Final Practical Exercise"
          />
        )}
      />

      {watch('content.core.fpe') ? (
        <>
          <NumberedHeading
            number="3"
            title="Path 1 Environment"
            instructions="Insert the page ID for the path 1 enviornment."
          />
          <Controller
            control={control}
            name="content.core.env_path_1"
            render={({ field }) => (
              <>
                <FormControl
                  className="mt-16"
                  fullWidth
                  size="small"
                  error={!!errors.content?.core?.env_path_1}
                >
                  <InputLabel id="environment-path-1-select-label">
                    Environment Path 1
                  </InputLabel>
                  <Select
                    labelId="environment-path-1-select-label"
                    id="environment-path-1-select"
                    {...field}
                    label="Environment Path 1"
                    value={
                      possiblePages.find((page) => page.id === field.value)
                        ? field.value
                        : ''
                    }
                  >
                    <MenuItem value="" disabled>
                      <em>Environment Path 1</em>
                    </MenuItem>
                    {possiblePages.map((page) => (
                      <MenuItem key={page.id} value={page.id}>
                        {page.label} - {page.title}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.content?.core?.env_path_1?.message}
                  </FormHelperText>
                </FormControl>
              </>
            )}
          />

          <NumberedHeading
            number="4"
            title="Path 2 Environment"
            instructions="Insert the page ID for the path 2 enviornment."
          />
          <Controller
            control={control}
            name="content.core.env_path_2"
            render={({ field }) => (
              <FormControl
                className="mt-16"
                fullWidth
                size="small"
                error={!!errors.content?.core?.env_path_2}
              >
                <InputLabel id="environment-path-2-select-label">
                  Environment Path 2
                </InputLabel>
                <Select
                  labelId="environment-path-2-select-label"
                  id="environment-path-2-select"
                  {...field}
                  label="Environment Path 2"
                  value={
                    possiblePages.find((page) => page.id === field.value)
                      ? field.value
                      : ''
                  }
                >
                  <MenuItem value="" disabled>
                    <em>Environment Path 2</em>
                  </MenuItem>
                  {possiblePages.map((page) => (
                    <MenuItem key={page.id} value={page.id}>
                      {page.label} - {page.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.content?.core?.env_path_2?.message}
                </FormHelperText>
              </FormControl>
            )}
          />

          <NumberedHeading
            number="5"
            title="Path 3 Environment"
            instructions="Insert the page ID for the path 3 enviornment."
          />
          <Controller
            control={control}
            name="content.core.env_path_3"
            render={({ field }) => (
              <FormControl
                className="mt-16"
                fullWidth
                size="small"
                error={!!errors.content?.core?.env_path_3}
              >
                <InputLabel id="environment-path-3-select-label">
                  Environment Path 3
                </InputLabel>
                <Select
                  labelId="environment-path-3-select-label"
                  id="environment-path-3-select"
                  {...field}
                  label="Environment Path 3"
                  value={
                    possiblePages.find((page) => page.id === field.value)
                      ? field.value
                      : ''
                  }
                >
                  {/* <MenuItem value="" disabled>
                    <em>Environment Path 3</em>
                  </MenuItem> */}
                  <MenuItem value="">
                    No Path 3 Environment
                  </MenuItem>
                  {possiblePages.map((page) => (
                    <MenuItem key={page.id} value={page.id}>
                      {page.label} - {page.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.content?.core?.env_path_3?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </>
      ) : (
        <>
          <NumberedHeading
            number="3"
            title="Job Aid Incomplete (TL)"
            instructions="If the learner has not earned the job aid, what message should be shown? Leave blank if this is a Final Practical Exercise."
          />
          <Controller
            control={control}
            name="content.core.no_jobaid_tl"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="No Job Aid"
                placeholder="No Job Aid"
                id="no_jobaid_tl"
                error={!!errors.content?.core?.no_jobaid_tl}
                helperText={errors.content?.core?.no_jobaid_tl?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
                dir={inputDir}
              />
            )}
          />

          <Divider className="my-16" />

          <NumberedHeading
            number="4"
            title="Job Aid Incomplete (EN)"
            instructions="If the learner has not earned the job aid, what message should be shown? Leave blank if this is a Final Practical Exercise."
          />
          <Controller
            control={control}
            name="content.core.no_jobaid_en"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="No Job Aid"
                placeholder="No Job Aid"
                id="no_jobaid_en"
                error={!!errors.content?.core?.no_jobaid_en}
                helperText={errors.content?.core?.no_jobaid_en?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
              />
            )}
          />

          <Divider className="my-16" />

          <NumberedHeading
            number="5"
            title="Job Aid Complete (TL)"
            instructions="If the learner has earned the job aid, what message should be shown? Leave blank if this is a Final Practical Exercise."
          />
          <Controller
            control={control}
            name="content.core.yes_jobaid_tl"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="Yes Job Aid"
                placeholder="Yes Job Aid"
                id="yes_jobaid_tl"
                error={!!errors.content?.core?.yes_jobaid_tl}
                helperText={errors.content?.core?.yes_jobaid_tl?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
                dir={inputDir}
              />
            )}
          />

          <NumberedHeading
            number="6"
            title="Job Aid Complete (EN)"
            instructions="If the learner has earned the job aid, what message should be shown? Leave blank if this is a Final Practical Exercise."
          />
          <Controller
            control={control}
            name="content.core.yes_jobaid_en"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="Yes Job Aid"
                placeholder="Yes Job Aid"
                id="yes_jobaid_en"
                error={!!errors.content?.core?.yes_jobaid_en}
                helperText={errors.content?.core?.yes_jobaid_en?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={5}
                maxRows={10}
              />
            )}
          />

          <Divider className="my-16" />

          <NumberedHeading
            number="7"
            title="Job Aid Lead (TL)"
            instructions="Enter a lead sentence for the Job Aide in the target language."
          />
          <Controller
            control={control}
            name="content.core.jobaid_pdf_lead_tl"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="Job Aid Lead (TL)"
                placeholder="Job Aid Lead (TL)"
                id="jobaid_pdf_lead_tl"
                error={!!errors.content?.core?.jobaid_pdf_lead_tl}
                helperText={errors.content?.core?.jobaid_pdf_lead_tl?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                dir={inputDir}
              />
            )}
          />

          <Divider className="my-16" />

          <NumberedHeading
            number="8"
            title="Job Aid Lead (EN)"
            instructions="Enter a lead sentence for the Job Aide in English."
          />
          <Controller
            control={control}
            name="content.core.jobaid_pdf_lead_en"
            render={({ field }) => (
              <TextField
                className="mt-16"
                {...field}
                label="Job Aid Lead (EN)"
                placeholder="Job Aid Lead (EN)"
                id="jobaid_pdf_lead_en"
                error={!!errors.content?.core?.jobaid_pdf_lead_en}
                helperText={errors.content?.core?.jobaid_pdf_lead_en?.message}
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
              />
            )}
          />

          <NumberedHeading
            number="9"
            title="Job Aid PDF Content"
            instructions="What is the content that should be in the Job Aid?"
          />
          <Controller
            control={control}
            name="content.jobaid_pdf"
            render={({ field }) => <JobAideArray {...{ control, errors }} />}
          />
        </>
      )}

      <Divider className="my-16" />

      <NumberedHeading
        number={watch('content.core.fpe') ? '6' : '10'}
        title="Activity Feedback"
        instructions="Using the controls below, add as many items as needed to provide results feedback for the activities in this ELO."
      />
      <Controller
        control={control}
        name="content.item_feedback"
        render={({ field }) => (
          <PEFeedbackArray
            inputDir={inputDir}
            {...{ control, errors }}
            possiblePages={possiblePages}
          />
        )}
      />
    </div>
  );
};

export default PEDebrief;
